// ============================================================================
// CANVAS RENDERER - 2D Rendering with Neon Effects
// ============================================================================

import { Vector2, Rectangle } from '@/shared/types';
import { COLORS } from '@/shared/constants';

export interface RenderLayer {
  name: string;
  zIndex: number;
  visible: boolean;
}

export interface Sprite {
  image: HTMLImageElement;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DrawTextOptions {
  font?: string;
  size?: number;
  color?: string;
  align?: CanvasTextAlign;
  baseline?: CanvasTextBaseline;
  glow?: boolean;
  glowColor?: string;
  glowSize?: number;
}

export interface DrawRectOptions {
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  glow?: boolean;
  glowColor?: string;
  glowSize?: number;
  cornerRadius?: number;
}

export interface DrawCircleOptions {
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  glow?: boolean;
  glowColor?: string;
  glowSize?: number;
}

export interface DrawLineOptions {
  color?: string;
  width?: number;
  glow?: boolean;
  glowColor?: string;
  glowSize?: number;
  dash?: number[];
}

export class Renderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  // Camera
  private cameraX = 0;
  private cameraY = 0;
  private cameraZoom = 1;
  private cameraShake: Vector2 = { x: 0, y: 0 };

  // Viewport
  private viewportWidth: number;
  private viewportHeight: number;

  constructor(canvas: HTMLCanvasElement, width: number, height: number) {
    this.canvas = canvas;
    this.viewportWidth = width;
    this.viewportHeight = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get 2D context');
    this.ctx = ctx;

    // Set canvas size
    this.resize(width, height);

    // Default settings
    this.ctx.imageSmoothingEnabled = false;
  }

  resize(width: number, height: number): void {
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.ctx.scale(dpr, dpr);
    this.viewportWidth = width;
    this.viewportHeight = height;
  }

  // -------------------------------------------------------------------------
  // Camera
  // -------------------------------------------------------------------------

  setCamera(x: number, y: number, zoom = 1): void {
    this.cameraX = x;
    this.cameraY = y;
    this.cameraZoom = zoom;
  }

  setCameraShake(x: number, y: number): void {
    this.cameraShake = { x, y };
  }

  getCameraOffset(): Vector2 {
    return {
      x: this.cameraX - this.viewportWidth / 2 / this.cameraZoom + this.cameraShake.x,
      y: this.cameraY - this.viewportHeight / 2 / this.cameraZoom + this.cameraShake.y,
    };
  }

  getCameraZoom(): number {
    return this.cameraZoom;
  }

  worldToScreen(worldPos: Vector2): Vector2 {
    const offset = this.getCameraOffset();
    return {
      x: (worldPos.x - offset.x) * this.cameraZoom,
      y: (worldPos.y - offset.y) * this.cameraZoom,
    };
  }

  screenToWorld(screenPos: Vector2): Vector2 {
    const offset = this.getCameraOffset();
    return {
      x: screenPos.x / this.cameraZoom + offset.x,
      y: screenPos.y / this.cameraZoom + offset.y,
    };
  }

  isInView(x: number, y: number, margin = 100): boolean {
    const screen = this.worldToScreen({ x, y });
    return (
      screen.x >= -margin &&
      screen.x <= this.viewportWidth + margin &&
      screen.y >= -margin &&
      screen.y <= this.viewportHeight + margin
    );
  }

  // -------------------------------------------------------------------------
  // Frame Control
  // -------------------------------------------------------------------------

  beginFrame(): void {
    this.ctx.save();
    this.clear();
  }

  endFrame(): void {
    this.ctx.restore();
  }

  beginWorld(): void {
    this.ctx.save();
    const offset = this.getCameraOffset();
    this.ctx.scale(this.cameraZoom, this.cameraZoom);
    this.ctx.translate(-offset.x, -offset.y);
  }

  endWorld(): void {
    this.ctx.restore();
  }

  clear(color = COLORS.BACKGROUND): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(0, 0, this.viewportWidth, this.viewportHeight);
  }

  // -------------------------------------------------------------------------
  // Basic Shapes
  // -------------------------------------------------------------------------

  drawRect(x: number, y: number, width: number, height: number, options: DrawRectOptions = {}): void {
    const {
      fill,
      stroke,
      strokeWidth = 1,
      glow = false,
      glowColor = COLORS.CYAN,
      glowSize = 10,
      cornerRadius = 0,
    } = options;

    this.ctx.save();

    if (glow) {
      this.ctx.shadowColor = glowColor;
      this.ctx.shadowBlur = glowSize;
    }

    if (cornerRadius > 0) {
      this.ctx.beginPath();
      this.ctx.roundRect(x, y, width, height, cornerRadius);
      if (fill) {
        this.ctx.fillStyle = fill;
        this.ctx.fill();
      }
      if (stroke) {
        this.ctx.strokeStyle = stroke;
        this.ctx.lineWidth = strokeWidth;
        this.ctx.stroke();
      }
    } else {
      if (fill) {
        this.ctx.fillStyle = fill;
        this.ctx.fillRect(x, y, width, height);
      }
      if (stroke) {
        this.ctx.strokeStyle = stroke;
        this.ctx.lineWidth = strokeWidth;
        this.ctx.strokeRect(x, y, width, height);
      }
    }

    this.ctx.restore();
  }

  drawCircle(x: number, y: number, radius: number, options: DrawCircleOptions = {}): void {
    const {
      fill,
      stroke,
      strokeWidth = 1,
      glow = false,
      glowColor = COLORS.CYAN,
      glowSize = 10,
    } = options;

    this.ctx.save();

    if (glow) {
      this.ctx.shadowColor = glowColor;
      this.ctx.shadowBlur = glowSize;
    }

    this.ctx.beginPath();
    this.ctx.arc(x, y, radius, 0, Math.PI * 2);

    if (fill) {
      this.ctx.fillStyle = fill;
      this.ctx.fill();
    }
    if (stroke) {
      this.ctx.strokeStyle = stroke;
      this.ctx.lineWidth = strokeWidth;
      this.ctx.stroke();
    }

    this.ctx.restore();
  }

  drawArc(x: number, y: number, radius: number, startAngle: number, endAngle: number, options: DrawCircleOptions = {}): void {
    const {
      fill,
      stroke,
      strokeWidth = 1,
      glow = false,
      glowColor = COLORS.CYAN,
      glowSize = 10,
    } = options;

    this.ctx.save();

    if (glow) {
      this.ctx.shadowColor = glowColor;
      this.ctx.shadowBlur = glowSize;
    }

    this.ctx.beginPath();
    this.ctx.arc(x, y, radius, startAngle, endAngle);

    if (fill) {
      this.ctx.fillStyle = fill;
      this.ctx.fill();
    }
    if (stroke) {
      this.ctx.strokeStyle = stroke;
      this.ctx.lineWidth = strokeWidth;
      this.ctx.stroke();
    }

    this.ctx.restore();
  }

  drawLine(x1: number, y1: number, x2: number, y2: number, options: DrawLineOptions = {}): void {
    const {
      color = COLORS.CYAN,
      width = 1,
      glow = false,
      glowColor = COLORS.CYAN,
      glowSize = 10,
      dash = [],
    } = options;

    this.ctx.save();

    if (glow) {
      this.ctx.shadowColor = glowColor;
      this.ctx.shadowBlur = glowSize;
    }

    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = width;
    this.ctx.setLineDash(dash);

    this.ctx.beginPath();
    this.ctx.moveTo(x1, y1);
    this.ctx.lineTo(x2, y2);
    this.ctx.stroke();

    this.ctx.restore();
  }

  drawPolygon(points: Vector2[], options: DrawRectOptions = {}): void {
    if (points.length < 3) return;

    const {
      fill,
      stroke,
      strokeWidth = 1,
      glow = false,
      glowColor = COLORS.CYAN,
      glowSize = 10,
    } = options;

    this.ctx.save();

    if (glow) {
      this.ctx.shadowColor = glowColor;
      this.ctx.shadowBlur = glowSize;
    }

    this.ctx.beginPath();
    this.ctx.moveTo(points[0]!.x, points[0]!.y);
    for (let i = 1; i < points.length; i++) {
      this.ctx.lineTo(points[i]!.x, points[i]!.y);
    }
    this.ctx.closePath();

    if (fill) {
      this.ctx.fillStyle = fill;
      this.ctx.fill();
    }
    if (stroke) {
      this.ctx.strokeStyle = stroke;
      this.ctx.lineWidth = strokeWidth;
      this.ctx.stroke();
    }

    this.ctx.restore();
  }

  // -------------------------------------------------------------------------
  // Text
  // -------------------------------------------------------------------------

  drawText(text: string, x: number, y: number, options: DrawTextOptions = {}): void {
    const {
      font = 'Segoe UI',
      size = 14,
      color = COLORS.TEXT_PRIMARY,
      align = 'left',
      baseline = 'top',
      glow = false,
      glowColor = COLORS.CYAN,
      glowSize = 10,
    } = options;

    this.ctx.save();

    if (glow) {
      this.ctx.shadowColor = glowColor;
      this.ctx.shadowBlur = glowSize;
    }

    this.ctx.font = `${size}px ${font}`;
    this.ctx.fillStyle = color;
    this.ctx.textAlign = align;
    this.ctx.textBaseline = baseline;
    this.ctx.fillText(text, x, y);

    this.ctx.restore();
  }

  measureText(text: string, font: string, size: number): TextMetrics {
    this.ctx.save();
    this.ctx.font = `${size}px ${font}`;
    const metrics = this.ctx.measureText(text);
    this.ctx.restore();
    return metrics;
  }

  // -------------------------------------------------------------------------
  // Sprites
  // -------------------------------------------------------------------------

  drawSprite(sprite: Sprite, x: number, y: number, width?: number, height?: number): void {
    const w = width ?? sprite.width;
    const h = height ?? sprite.height;
    this.ctx.drawImage(
      sprite.image,
      sprite.x,
      sprite.y,
      sprite.width,
      sprite.height,
      x,
      y,
      w,
      h
    );
  }

  drawImage(
    image: HTMLImageElement | HTMLCanvasElement,
    x: number,
    y: number,
    width?: number,
    height?: number,
    rotation = 0
  ): void {
    const w = width ?? image.width;
    const h = height ?? image.height;

    this.ctx.save();
    this.ctx.translate(x + w / 2, y + h / 2);
    this.ctx.rotate(rotation);
    this.ctx.drawImage(image, -w / 2, -h / 2, w, h);
    this.ctx.restore();
  }

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------

  drawNeonRect(rect: Rectangle, color: string, intensity = 1): void {
    const { x, y, width, height } = rect;
    
    // Outer glow
    this.drawRect(x, y, width, height, {
      stroke: color,
      strokeWidth: 2,
      glow: true,
      glowColor: color,
      glowSize: 15 * intensity,
    });
    
    // Inner fill with transparency
    this.drawRect(x + 2, y + 2, width - 4, height - 4, {
      fill: `${color}22`,
    });
  }

  drawHealthBar(
    x: number,
    y: number,
    width: number,
    height: number,
    current: number,
    max: number,
    shield = 0,
    maxShield = 0
  ): void {
    const healthPercent = Math.max(0, current / max);
    const shieldPercent = maxShield > 0 ? Math.max(0, shield / maxShield) : 0;

    // Background
    this.drawRect(x, y, width, height, {
      fill: '#000000AA',
      stroke: '#333',
      strokeWidth: 1,
    });

    // Health bar
    if (healthPercent > 0) {
      const healthWidth = (width - 2) * healthPercent;
      this.drawRect(x + 1, y + 1, healthWidth, height - 2, {
        fill: COLORS.HEALTH,
      });
    }

    // Shield bar (on top)
    if (shieldPercent > 0) {
      const shieldWidth = (width - 2) * shieldPercent;
      this.drawRect(x + 1, y + 1, shieldWidth, height - 2, {
        fill: COLORS.SHIELD,
      });
    }
  }

  drawAbilityCooldown(
    x: number,
    y: number,
    radius: number,
    cooldownPercent: number,
    icon?: HTMLImageElement
  ): void {
    // Background
    this.drawCircle(x, y, radius, {
      fill: '#222',
      stroke: COLORS.CYAN,
      strokeWidth: 2,
    });

    // Icon
    if (icon) {
      const iconSize = radius * 1.4;
      this.drawImage(icon, x - iconSize / 2, y - iconSize / 2, iconSize, iconSize);
    }

    // Cooldown overlay
    if (cooldownPercent > 0) {
      this.ctx.save();
      this.ctx.globalAlpha = 0.7;
      this.ctx.fillStyle = '#000';
      this.ctx.beginPath();
      this.ctx.moveTo(x, y);
      this.ctx.arc(
        x,
        y,
        radius,
        -Math.PI / 2,
        -Math.PI / 2 + Math.PI * 2 * cooldownPercent,
        false
      );
      this.ctx.closePath();
      this.ctx.fill();
      this.ctx.restore();
    }
  }

  // -------------------------------------------------------------------------
  // Debug
  // -------------------------------------------------------------------------

  drawDebugGrid(cellSize: number, color = '#333'): void {
    const offset = this.getCameraOffset();
    const startX = Math.floor(offset.x / cellSize) * cellSize;
    const startY = Math.floor(offset.y / cellSize) * cellSize;
    const endX = offset.x + this.viewportWidth / this.cameraZoom + cellSize;
    const endY = offset.y + this.viewportHeight / this.cameraZoom + cellSize;

    this.ctx.save();
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = 0.5;

    for (let x = startX; x <= endX; x += cellSize) {
      this.drawLine(x, startY, x, endY, { color, width: 0.5 });
    }
    for (let y = startY; y <= endY; y += cellSize) {
      this.drawLine(startX, y, endX, y, { color, width: 0.5 });
    }

    this.ctx.restore();
  }

  drawDebugPoint(x: number, y: number, color = COLORS.CYAN, label?: string): void {
    this.drawCircle(x, y, 4, { fill: color });
    if (label) {
      this.drawText(label, x + 8, y - 8, { color, size: 10 });
    }
  }

  drawDebugRect(rect: Rectangle, color = COLORS.CYAN, label?: string): void {
    this.drawRect(rect.x, rect.y, rect.width, rect.height, {
      stroke: color,
      strokeWidth: 1,
    });
    if (label) {
      this.drawText(label, rect.x, rect.y - 14, { color, size: 10 });
    }
  }

  // -------------------------------------------------------------------------
  // Getters
  // -------------------------------------------------------------------------

  getWidth(): number {
    return this.viewportWidth;
  }

  getHeight(): number {
    return this.viewportHeight;
  }

  getContext(): CanvasRenderingContext2D {
    return this.ctx;
  }
}
